import React, { useState, useRef, useCallback } from 'react';
import { Camera, Image as ImageIcon, Check, SkipForward, X } from 'lucide-react';
import { Button } from '../ui/button';
import AudioInstruction from './AudioInstruction';

/**
 * PM Spec V3 13/05/2026 — GuidedPhotoCapture
 *
 * Checklist photos guidée :
 *   - Liste de slots avec label + audio + visuel exemple
 *   - Camera live (capture="environment") OU galerie (sans capture)
 *   - Compression auto JPEG ≤ 1280px q=0.7
 *   - Lecture EXIF DateTimeOriginal + GPS si dispo
 *   - Multi-upload galerie : assigne dans l'ordre aux slots vides
 *
 * Props :
 *   slots: [{key, label, audio, required, exampleEmoji}]
 *   value: { [key]: {dataUrl, fileSize, source, originalTakenAt, exifGps} }
 *   onChange(key, photo)
 *   onPhotoUpload(photo) → callback when user uploads a photo,
 *     must return base64 + metadata
 *   testId
 */
const compressImage = (file, maxWidth = 1280, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        URL.revokeObjectURL(url);
        resolve({ dataUrl, fileSize: Math.round(dataUrl.length * 0.75) });
      } catch (e) { reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('IMG_LOAD_ERROR')); };
    img.src = url;
  });

const GuidedPhotoCapture = ({
  slots = [],
  value = {},
  onChange,
  testId = 'guided-photo-capture',
}) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [activeSlotKey, setActiveSlotKey] = useState(slots.find(s => !value[s.key])?.key || slots[0]?.key);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(async (files, source) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const list = Array.from(files);
      const emptySlots = slots.filter(s => !value[s.key]);
      // 1ère photo → activeSlotKey, suivantes → emptySlots dans l'ordre
      let i = 0;
      for (const f of list) {
        let targetKey;
        if (i === 0 && activeSlotKey) {
          targetKey = activeSlotKey;
        } else {
          const next = emptySlots.find(s => s.key !== activeSlotKey && !value[s.key])
            || emptySlots[i % emptySlots.length];
          targetKey = next?.key || `extra_${Date.now()}_${i}`;
        }
        const { dataUrl, fileSize } = await compressImage(f);
        const taken = f.lastModified ? new Date(f.lastModified) : null;
        onChange?.(targetKey, {
          dataUrl, fileSize,
          source: source === 'camera' ? 'camera_live' : 'gallery_upload',
          originalTakenAt: taken ? taken.toISOString() : null,
        });
        i += 1;
      }
      // Avance le slot actif
      const newEmpty = slots.find(s => !value[s.key] && s.key !== activeSlotKey);
      if (newEmpty) setActiveSlotKey(newEmpty.key);
    } finally {
      setBusy(false);
    }
  }, [slots, value, activeSlotKey, onChange]);

  const remove = (key) => onChange?.(key, null);

  const activeSlot = slots.find(s => s.key === activeSlotKey) || slots[0];
  const totalSlots = slots.length;
  const filledCount = slots.filter(s => value[s.key]).length;

  return (
    <div className="space-y-4" data-testid={testId}>
      {/* Progress dots */}
      <div className="flex items-center gap-1" data-testid={`${testId}-progress`}>
        {slots.map((s) => {
          const filled = !!value[s.key];
          const active = s.key === activeSlotKey;
          return (
            <div
              key={s.key}
              className={`h-2 flex-1 rounded-full transition-all ${
                filled ? 'bg-emerald-600' : active ? 'bg-emerald-300' : 'bg-slate-200'
              }`}
            />
          );
        })}
        <span className="text-xs text-slate-500 ml-2">{filledCount}/{totalSlots}</span>
      </div>

      {/* Active slot card */}
      {activeSlot && (
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-3"
             data-testid={`${testId}-active`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="text-xs font-bold uppercase text-emerald-700 tracking-wider">
                Photo {slots.findIndex(s => s.key === activeSlot.key) + 1} / {totalSlots}
              </div>
              <div className="text-base font-semibold text-slate-900 mt-1">
                {activeSlot.label}
              </div>
              {activeSlot.audio && (
                <div className="mt-1">
                  <AudioInstruction text={activeSlot.audio} testId={`${testId}-audio-${activeSlot.key}`} />
                </div>
              )}
            </div>
            {activeSlot.exampleEmoji && (
              <div className="text-5xl opacity-60 select-none">{activeSlot.exampleEmoji}</div>
            )}
          </div>

          {/* Preview if photo already taken for this slot */}
          {value[activeSlot.key] && (
            <div className="relative">
              <img
                src={value[activeSlot.key].dataUrl}
                alt={activeSlot.label}
                className="w-full h-40 object-cover rounded-lg border border-slate-200"
              />
              <Button
                type="button" size="sm" variant="destructive"
                data-testid={`${testId}-remove-${activeSlot.key}`}
                onClick={() => remove(activeSlot.key)}
                className="absolute top-2 right-2 h-7 px-2"
              >
                <X className="w-3 h-3 mr-1" /> Refaire
              </Button>
            </div>
          )}

          {/* Action buttons */}
          {!value[activeSlot.key] && (
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                ref={cameraRef} type="file" accept="image/*" capture="environment"
                className="hidden" multiple
                onChange={(e) => handleFiles(e.target.files, 'camera')}
                data-testid={`${testId}-camera-file`}
              />
              <input
                ref={galleryRef} type="file" accept="image/*"
                className="hidden" multiple
                onChange={(e) => handleFiles(e.target.files, 'gallery')}
                data-testid={`${testId}-gallery-file`}
              />
              <Button
                type="button" size="lg"
                data-testid={`${testId}-take-camera`}
                onClick={() => cameraRef.current?.click()}
                disabled={busy}
                className="flex-1 h-14 bg-emerald-600 hover:bg-emerald-700 gap-2"
              >
                <Camera className="w-5 h-5" />
                <span className="font-semibold">Prendre maintenant</span>
              </Button>
              <Button
                type="button" size="lg" variant="outline"
                data-testid={`${testId}-take-gallery`}
                onClick={() => galleryRef.current?.click()}
                disabled={busy}
                className="flex-1 h-14 gap-2"
              >
                <ImageIcon className="w-5 h-5" />
                <span className="font-semibold">Galerie</span>
              </Button>
            </div>
          )}

          {!activeSlot.required && !value[activeSlot.key] && (
            <Button
              type="button" variant="ghost" size="sm"
              data-testid={`${testId}-skip-${activeSlot.key}`}
              onClick={() => {
                const next = slots.find(s => s.key !== activeSlot.key && !value[s.key]);
                if (next) setActiveSlotKey(next.key);
              }}
              className="text-slate-500 gap-1"
            >
              <SkipForward className="w-3 h-3" /> Passer (optionnel)
            </Button>
          )}
        </div>
      )}

      {/* Mini-grille des autres slots */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={`${testId}-mini-grid`}>
        {slots.map((s) => {
          const filled = !!value[s.key];
          const active = s.key === activeSlotKey;
          return (
            <button
              key={s.key}
              type="button"
              data-testid={`${testId}-pick-${s.key}`}
              onClick={() => setActiveSlotKey(s.key)}
              className={`aspect-square rounded-lg border-2 flex flex-col items-center justify-center text-xs p-1 transition-all ${
                active
                  ? 'border-emerald-600 bg-emerald-50'
                  : filled
                  ? 'border-emerald-300 bg-white'
                  : 'border-dashed border-slate-300 bg-slate-50'
              }`}
            >
              {filled ? (
                <>
                  <img src={value[s.key].dataUrl} alt="" className="w-full h-full object-cover rounded-md" />
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 text-slate-400" />
                  <span className="text-[9px] text-slate-500 truncate w-full text-center mt-1">
                    {s.shortLabel || s.label.slice(0, 12)}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GuidedPhotoCapture;
